import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import AdCardMetrics from './AdCardMetrics'
import AdCardPreview from './AdCardPreview'
import AdCardDetails from '../../../../../features/components/AdCardListItem/AdCardDetails'
import SkeletonFacebookAdCardListItem from './Skeleton'
import ActionsDropdown from '../../../../../features/components/ActionsDropdown'
import InternalAdActionButtons from '../../../../AdApprovals/AdsSection/InternalAdActionButtons'
import AdCardListItem from '../../../../../features/components/AdCardListItem'
import AdStatusChip from './AdCardStatuses/AdStatusChip'
import EffectiveStatusChip from './AdCardStatuses/EffectiveStatusChip'

import useFacebookAdActions from './useFacebookAdActions'
import useInternalAdSelfAccountName from '../../../../../features/hooks/internalAdHooks/useInternalAdSelfAccountName'
import { getPlacementPositions } from '../../../../../forms/Facebook/AdForms/AdFacebookEdit/helpers'
import { useClearApproveInternalAd } from '../../../../AdApprovals/AdsSection/hooks'

import {
  adDeleteIsLoadingSelector,
  adDeleteItemIdSelector,
  adUpdateIdSelector,
  adUpdateIsLoadingSelector,
  approveInternalAdIsLoadingSelector,
  disapproveInternalAdIsLoadingSelector
} from '../../../../../modules/selectors/ads'
import { lineItemSelector, lineItemsSelector } from '../../../../../modules/selectors/lineItems'
import { selectedPartnershipSelector } from '../../../../../modules/selectors/partnerships'

import useStyles from './styles'

function FacebookAdCardListItem({
  ad,
  currencySymbol,
  showStatus = true,
  showDetails = true,
  showMetrics = true,
  showActionsDropdown = true,
  showActionsButtons = false,
  supportedActions,
  checkPlacements,
  isLineItemSummaryPage,
  isAdApprovalsStyle,
  disapproveInternalAdHandler,
  providedPlacementPositions,
  handleDeleteRequest
}) {
  const classes = useStyles({ hasTopNavbar: showStatus || showActionsDropdown })

  const lineItem = useSelector(lineItemSelector)
  const lineItems = useSelector(lineItemsSelector)
  const updateAdId = useSelector(adUpdateIdSelector)
  const updateAdIsLoading = useSelector(adUpdateIsLoadingSelector)
  const deleteAdId = useSelector(adDeleteItemIdSelector)
  const deleteAdIsLoading = useSelector(adDeleteIsLoadingSelector)

  const internalAdIsApproving = useSelector(approveInternalAdIsLoadingSelector(ad.id))
  const internalAdIsDisapproving = useSelector(disapproveInternalAdIsLoadingSelector(ad.id))
  useClearApproveInternalAd(ad.id)

  const adSelfAccountName = useInternalAdSelfAccountName(ad)

  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const { spend_metrics: showAdditionalMetrics } = selectedPartnership

  const { actionsDropdownOptions, approveInternalAdHandler, editInternalAdHandler } = useFacebookAdActions({
    ad,
    handleDeleteRequest,
    supportedActions
  })

  const placementPositions = useMemo(() => {
    if (providedPlacementPositions) {
      return providedPlacementPositions
      // todo PBR-1012 refactor to pass placementPositions only from parent component
    } else if (checkPlacements) {
      return isLineItemSummaryPage
        ? getPlacementPositions({ ad, lineItem, lineItemWasLoaded: true })
        : getPlacementPositions({ ad, lineItems, lineItemsWasLoaded: true })
    } else {
      return []
    }
  }, [providedPlacementPositions, checkPlacements, isLineItemSummaryPage, ad, lineItem, lineItems])

  const { id, name, line_item_name: lineItemName } = ad

  // We show adSelfAccountName on AdApprovals page
  const subTitle = isAdApprovalsStyle ? adSelfAccountName : lineItemName

  const adIsUpdating = updateAdIsLoading && updateAdId === id
  const adIsDeleting = deleteAdIsLoading && deleteAdId === id

  if (adIsUpdating || adIsDeleting || internalAdIsApproving || internalAdIsDisapproving) {
    return (
      <SkeletonFacebookAdCardListItem
        showStatus={showStatus}
        showDetails={showDetails}
        showMetrics={showMetrics}
        showActionsDropdown={showActionsDropdown}
        showActionsButtons={showActionsButtons}
        isAdApprovalsStyle={isAdApprovalsStyle}
      />
    )
  }

  return (
    <AdCardListItem>
      {(showStatus || showActionsDropdown) && (
        <div className={classes.cardHeader}>
          {showStatus && (
            <div>
              <AdStatusChip ad={ad} />
              <EffectiveStatusChip ad={ad} />
            </div>
          )}
          {showActionsDropdown && actionsDropdownOptions.length && (
            <ActionsDropdown
              options={actionsDropdownOptions}
              actionsListWrapperClassName={classes.actionsListWrapper}
              className={classes.actionsDropdownMenu}
              closeOnInsideClick
            />
          )}
        </div>
      )}

      <AdCardPreview ad={ad} placementPositions={placementPositions} isAdApprovalsStyle={isAdApprovalsStyle} />

      {showDetails && <AdCardDetails title={name} subTitle={subTitle} />}

      {showActionsButtons && (
        <InternalAdActionButtons
          approveInternalAdHandler={approveInternalAdHandler}
          editInternalAdHandler={editInternalAdHandler}
          disapproveInternalAdHandler={() => disapproveInternalAdHandler(ad)}
        />
      )}
      {showMetrics && (
        <AdCardMetrics ad={ad} currencySymbol={currencySymbol} showAdditionalMetrics={showAdditionalMetrics} />
      )}
    </AdCardListItem>
  )
}

export default FacebookAdCardListItem

FacebookAdCardListItem.propTypes = {
  ad: PropTypes.object.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  showStatus: PropTypes.bool,
  showDetails: PropTypes.bool,
  showMetrics: PropTypes.bool,
  showActionsDropdown: PropTypes.bool,
  supportedActions: PropTypes.object,
  showActionsButtons: PropTypes.bool,
  isLineItemSummaryPage: PropTypes.bool,
  isAdApprovalsStyle: PropTypes.bool,
  disapproveInternalAdHandler: PropTypes.func
}
